import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { GlobalAsset, Organization } from '@types';
import { organizationGlobalAssets } from '../common/organizationGlobalAssets';

interface GlobalAssetContextType {
    assets: GlobalAsset[];
    getAssetsByClient: (clientId: string) => GlobalAsset[];
    getPrimaryAsset: (clientId: string) => GlobalAsset | undefined;
}

export const GlobalAssetContext = createContext<GlobalAssetContextType | undefined>(undefined);

export const useGlobalAssets = () => {
    const context = useContext(GlobalAssetContext);
    if (!context)
        throw new Error('useGlobalAssets must be used within a GlobalAssetProvider');
    return context;
};

export const GlobalAssetProvider: React.FC<{ children: ReactNode; organization?: Organization | null }> = ({ children, organization }) => {
    const value = useMemo(() => {
        const assets = organization ? organizationGlobalAssets(organization) : [];
        return {
            assets,
            getAssetsByClient: (clientId: string) => organization?.id === clientId ? assets : [],
            getPrimaryAsset: (clientId: string) => organization?.id === clientId ? assets.find(asset => asset.isPrimary) : undefined,
        };
    }, [organization]);

    return <GlobalAssetContext.Provider value={value}>{children}</GlobalAssetContext.Provider>;
};
